'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */

import { Dialog, IconButton } from '@material-tailwind/react';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import Close from '../icons/Close';

export type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  className?: string;
  hideCloseButton?: boolean;
  /**
   * Mobile bottom sheet (CMS-1818). Below our `md` breakpoint the panel is
   * pinned to the bottom of the *visual* viewport (`position: fixed`, capped at
   * `92svh`) — full width, rounded top corners only, sliding up — and shows a
   * drag handle. From `md` up it stays the centered modal.
   * The sheet geometry lives in `cms.css` (`.mhg-modal-sheet`), where the
   * breakpoint comes from the Tailwind config and the overrides can beat
   * Material Tailwind's own panel classes.
   */
  sheetOnMobile?: boolean;
  // Forwarded onto the Dialog root so callers can attach test hooks
  // (e.g. `data-component="apply-v3-modal"`) without an extra wrapper node.
  // Same passthrough the existing `data-mhg-modal` marker relies on. Numbers
  // and booleans are allowed because state flags read naturally as
  // `data-selected={isSelected}`; React stringifies them.
} & {
  [dataAttr: `data-${string}`]: string | number | boolean | undefined;
};

/**
 * Generic centered modal wrapper over Material Tailwind Dialog.
 *
 * Material Tailwind Dialog already provides focus trap + Esc + click-outside +
 * portal. On top of that this wrapper adds body scroll-lock with scrollbar-
 * width compensation (pattern from `AsideModal`) and a close button.
 *
 * Created for CMS-1740 apply-v3 modal but kept generic — any future centered
 * modal (vs. side-aside) should reuse this.
 *
 * The `data-mhg-modal` marker pulls the Material Tailwind `FloatingOverlay`
 * (hardcoded inline `z-index: 9999`, in the dropdown layer) down to the
 * design-system modal layer (`--z-index-modal`) via a scoped rule in
 * `cms.css`, so tooltips opened inside the modal (`--z-index-modal-tooltip`)
 * layer above it instead of underneath.
 */
export const Modal: FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  className,
  hideCloseButton,
  sheetOnMobile,
  ...rest
}) => {
  useBodyScrollLock(isOpen);

  return (
    <Dialog
      open={isOpen}
      handler={onClose}
      {...rest}
      // `rest` is spread first so the internal marker always wins: it drives
      // the z-index rule below and must not be clobbered by a caller's
      // `data-*` spread.
      data-mhg-modal
      // Material Tailwind's default enter/exit drops the panel in from above
      // (`y: -50`). The sheet flavour rises instead — a short fade-up on desktop,
      // full sheet-up on mobile (the mobile distance comes from the CSS
      // animation, which outranks this inline transform while it runs).
      animate={
        sheetOnMobile
          ? {
              mount: { opacity: 1, y: 0 },
              unmount: { opacity: 0, y: 16 },
            }
          : undefined
      }
      className={classNames(
        // Material Tailwind's FloatingFocusManager focuses the dialog panel
        // (tabindex=-1) on open, which paints the browser's default focus
        // outline — most visible when the modal opens from a direct URL. Focus
        // is trapped inside and interactive children keep their own focus
        // rings, so the container outline is just noise.
        'focus:outline-none',
        // eslint-disable-next-line tailwindcss/no-arbitrary-value
        'max-h-[90vh] overflow-y-auto',
        // eslint-disable-next-line tailwindcss/no-custom-classname
        sheetOnMobile && 'mhg-modal-sheet',
        // Width is left to the caller via `className`; without it the
        // Material Tailwind Dialog default size applies.
        className,
      )}
    >
      {sheetOnMobile && (
        // Sheet affordance; the stylesheet hides it above the sheet breakpoint.
        // eslint-disable-next-line tailwindcss/no-custom-classname
        <div aria-hidden className="mhg-modal-sheet-handle" />
      )}
      {!hideCloseButton && (
        <div className="absolute right-4 top-4 z-10">
          <IconButton variant="text" onClick={onClose} className="!size-6">
            <Close className="flex size-6 text-gray-900" />
          </IconButton>
        </div>
      )}
      {children}
    </Dialog>
  );
};
